import {
  normalizeAiPhotoConfigId,
  PublicAiPhotoCatalogSchema, SCENE_CATALOG, STYLE_CATALOG,
  type AiPhotoScope, type PublicAiPhotoCatalog, type PublicAiPhotoLook,
} from '@ai-photo-style/shared';
import type { EmbedConfig, EmbedVariant } from './config';
import { EmbedError, type ErrorCode } from './errors';

/** Only display metadata; the legacy fallback contains no prompts or output policy. */
export const LEGACY_LOOKS: PublicAiPhotoLook[] = [
  ...STYLE_CATALOG.map((look, order): PublicAiPhotoLook => ({
    ...look, kind: 'style', enabled: true, order, previewUrl: null,
    groups: look.id === 'vintage-waterverf' ? ['stijlen', 'scenes'] : ['stijlen'],
    ...(look.id === 'vintage-waterverf' ? {
      sceneLabel: 'Gezin in waterverf',
      sceneDescription: 'Het gezin blijft zoals op de foto, in vintage waterverf.',
    } : {}),
  })),
  ...SCENE_CATALOG.map((look, order): PublicAiPhotoLook => ({
    ...look, kind: 'scene', enabled: true, order: order + 10, groups: ['scenes'], previewUrl: null,
  })),
];

export function parseCatalog(value: unknown): PublicAiPhotoCatalog {
  const parsed = PublicAiPhotoCatalogSchema.safeParse(value);
  if (!parsed.success || new Set(parsed.data.looks.map((look) => look.id)).size !== parsed.data.looks.length) {
    throw new EmbedError('CATALOG_INVALID');
  }
  return parsed.data;
}

export function originScope(origin: string): AiPhotoScope | undefined {
  const scopes: Record<string, AiPhotoScope> = {
    'https://hippe-geboortekaartjes.nl': { shopId: 'hippe', environmentId: 'production' },
    'https://www.hippe-geboortekaartjes.nl': { shopId: 'hippe', environmentId: 'production' },
    'https://hippegeboortekaartjes.nl': { shopId: 'hippe', environmentId: 'production' },
    'https://test-hg.kaart-o-matic.nl': { shopId: 'hippe', environmentId: 'acceptance' },
    'https://madeformoments.nl': { shopId: 'mfm', environmentId: 'production' },
    'https://www.madeformoments.nl': { shopId: 'mfm', environmentId: 'production' },
    'https://prettyorange.fr': { shopId: 'po-fr', environmentId: 'production' },
    'https://www.prettyorange.fr': { shopId: 'po-fr', environmentId: 'production' },
    'https://prettyorange.de': { shopId: 'po-de', environmentId: 'production' },
    'https://www.prettyorange.de': { shopId: 'po-de', environmentId: 'production' },
  };
  return scopes[origin];
}

export interface ResolvedCatalog {
  catalog?: PublicAiPhotoCatalog;
  looks: PublicAiPhotoLook[];
  choices: PublicAiPhotoLook[];
  variant: EmbedVariant;
  fixedLook: string | null;
  error?: ErrorCode;
}

export function presentLook(look: PublicAiPhotoLook, variant: EmbedVariant): PublicAiPhotoLook {
  return variant === 'scenes'
    ? { ...look, label: look.sceneLabel ?? look.label, description: look.sceneDescription ?? look.description }
    : look;
}

/** Also used for fixture configs without a catalog; production boot always fetches first. */
export function resolveCatalog(cfg: EmbedConfig): ResolvedCatalog {
  let catalog: PublicAiPhotoCatalog | undefined;
  try {
    const configId = normalizeAiPhotoConfigId(cfg.configId);
    if (cfg.catalog !== undefined) catalog = parseCatalog(cfg.catalog);
    if (catalog && normalizeAiPhotoConfigId(catalog.configId) !== configId) throw new EmbedError('CATALOG_INVALID');
  } catch {
    return { looks: [], choices: [], variant: cfg.variant, fixedLook: null, error: 'CATALOG_INVALID' };
  }
  if (cfg.configId !== undefined) {
    if (!catalog) return { looks: [], choices: [], variant: 'stijlen', fixedLook: null, error: 'CATALOG_UNAVAILABLE' };
    // A tool config is the entire selection, independent of legacy variant/group/billing hints.
    const choices = catalog.looks.filter((look) => look.enabled)
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    const fixedLook = choices.length === 1 ? choices[0]!.id : null;
    const variant = choices.length > 0 && choices.every((look) => look.kind === 'scene') ? 'scenes' : 'stijlen';
    const error = !catalog.enabled ? 'CATALOG_DISABLED' : choices.length === 0 ? 'CATALOG_EMPTY' : undefined;
    return { catalog, looks: catalog.looks, choices, variant, fixedLook, error };
  }
  const looks = catalog?.looks ?? LEGACY_LOOKS;
  const requested = cfg.requestedVariant;
  const fixedLook = requested && requested !== 'stijlen' && requested !== 'scenes' ? requested : cfg.fixedLook;
  const fixed = looks.find((look) => look.id === fixedLook);
  const variant = fixed ? (fixed.kind === 'scene' ? 'scenes' : 'stijlen') : cfg.variant;
  const choices = looks.filter((look) => look.enabled && look.groups.includes(variant))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .map((look) => presentLook(look, variant));
  const error: ErrorCode | undefined = catalog && !catalog.enabled ? 'CATALOG_DISABLED'
    : fixedLook && !fixed?.enabled ? 'CATALOG_LOOK_UNAVAILABLE'
    : !fixedLook && choices.length === 0 ? 'CATALOG_EMPTY' : undefined;
  return { catalog, looks, choices, variant, fixedLook, error };
}

/** No network/schema-error fallback, and an old API must never turn MF into HG. */
export async function loadCatalog(cfg: EmbedConfig, client: { getCatalog(): Promise<PublicAiPhotoCatalog | null> }): Promise<EmbedConfig> {
  let configId: string;
  try { configId = normalizeAiPhotoConfigId(cfg.configId); }
  catch { throw new EmbedError('CATALOG_INVALID'); }
  const catalog = await client.getCatalog();
  const expected = originScope(location.origin);
  if (catalog === null) {
    if (cfg.configId !== undefined) throw new EmbedError('CATALOG_UNAVAILABLE');
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
    if (expected?.shopId !== 'hippe' && !local) throw new EmbedError('CATALOG_UNAVAILABLE');
    return { ...cfg, catalog: undefined };
  }
  const validated = parseCatalog(catalog);
  if (normalizeAiPhotoConfigId(validated.configId) !== configId) throw new EmbedError('CATALOG_INVALID');
  if (expected && (expected.shopId !== validated.shopId || expected.environmentId !== validated.environmentId)) {
    throw new EmbedError('CATALOG_INVALID');
  }
  return { ...cfg, catalog: validated };
}